import { NormalizedFormState, NormalizedFieldState, NormalizedSection, NormalizedRowConfig } from '../config.models';
import { RowItemType } from '../models';
import { TritonMonitorConfiguration } from './triton/models';
import { SelectField, FieldType } from '../models/field-models';
import { SelectableOption, SelectableValue } from '../models/selectables';
import { MonitorSeries } from '../../location-card.constants';

function searchInSection(section: NormalizedSection, fieldId: string): NormalizedFieldState | undefined {
    // Check direct fields in this section first
    if (section.fields && section.fields[fieldId]) {
        return section.fields[fieldId];
    }

    // Check for nested section fields
    for (const field of Object.values(section.fields || {})) {
        // If this field is itself a section, check if it contains our target field
        if (field.type === RowItemType.Section && field.section) {
            const result = searchInSection(field.section, fieldId);
            if (result) {
                return result;
            }
        }
    }

    return undefined;
}

// Helper function to search for a section by ID
function searchForSection(section: NormalizedSection, sectionId: string): NormalizedSection | undefined {
    // Check if this is the section we're looking for
    if (section.id === sectionId) {
        return section;
    }

    // Check nested sections
    for (const field of Object.values(section.fields || {})) {
        if (field.type === RowItemType.Section && field.section) {
            const result = searchForSection(field.section, sectionId);
            if (result) {
                return result;
            }
        }
    }

    return undefined;
}

// Helper function to search for a row by ID in a section
function searchRowInSection(section: NormalizedSection, rowId: string): NormalizedRowConfig | undefined {
    // Check direct rows in this section
    if (section.rows) {
        for (const row of section.rows) {
            if (row.id === rowId) {
                return row;
            }
        }
    }

    // Check for nested section rows
    for (const field of Object.values(section.fields || {})) {
        if (field.type === RowItemType.Section && field.section) {
            const result = searchRowInSection(field.section, rowId);
            if (result) {
                return result;
            }
        }
    }

    return undefined;
}

export function assertFieldType<T extends NormalizedFieldState['type']>(
    field: NormalizedFieldState | undefined,
    fieldType: T,
): Extract<NormalizedFieldState, { type: T }> | undefined {
    return field?.type === fieldType ? (field as Extract<NormalizedFieldState, { type: T }>) : undefined;
}

/**
 * Finds a field by ID throughout the entire form structure
 */
export function findFieldById(state: NormalizedFormState, fieldId: string): NormalizedFieldState | undefined;

/**
 * Finds a field by ID with type checking
 * Returns a strongly-typed field when type parameter is provided
 */
export function findFieldById<T extends NormalizedFieldState['type']>(
    state: NormalizedFormState,
    fieldId: string,
    fieldType: T,
): Extract<NormalizedFieldState, { type: T }> | undefined;

/**
 * Implementation that handles both cases
 */
export function findFieldById<T extends NormalizedFieldState['type']>(
    state: NormalizedFormState,
    fieldId: string,
    fieldType?: T,
): NormalizedFieldState | Extract<NormalizedFieldState, { type: T }> | undefined {
    // Search through all top-level sections
    for (const section of state.config.sections || []) {
        const result = searchInSection(section, fieldId);
        if (result) {
            if (fieldType !== undefined) {
                return assertFieldType(result, fieldType);
            }
            return result;
        }
    }

    return undefined;
}

/**
 * Finds a section by ID throughout the entire form structure
 * including nested sections
 */
export function findSectionById(state: NormalizedFormState, sectionId: string): NormalizedSection | undefined {
    // Search through all top-level sections
    for (const section of state.config.sections || []) {
        const result = searchForSection(section, sectionId);
        if (result) {
            return result;
        }
    }

    return undefined;
}

/**
 * Finds a row by ID throughout the entire form structure
 * including nested sections
 */
export function findRowById(state: NormalizedFormState, rowId: string): NormalizedRowConfig | undefined {
    // Search through all top-level sections
    for (const section of state.config.sections || []) {
        const result = searchRowInSection(section, rowId);
        if (result) {
            return result;
        }
    }

    return undefined;
}

/**
 * Finds a select field by ID with precise generic typing
 * @param state The normalized form state
 * @param fieldId The field ID to search for
 * @returns A strongly-typed SelectField or undefined
 */
export function findSelectFieldById<T extends SelectableOption, V extends SelectableValue>(
    state: NormalizedFormState,
    fieldId: string,
): SelectField<T, V> | undefined {
    const field = findFieldById(state, fieldId, FieldType.Select);
    return field as SelectField<T, V> | undefined;
}

export function isTritonConfig(
    config: unknown
): config is TritonMonitorConfiguration {
    return config !== null && typeof config === 'object' && 'devices' in config;
}

export function normalizeSeriesName(seriesValue: string): string {
    const match = MonitorSeries.find((s) => s.value === seriesValue);
    return match ? match.text : seriesValue;
}
